import { defineStore } from "pinia";
import { rimuoviServiceWorkerObsoleti } from "~/utils/serviceWorkerObsoleti";

// Notifiche push (Web Push) di questo dispositivo: supporto del browser, permesso, iscrizione. Lo stato è condiviso tra la scheda
// del profilo (riepilogo) e il pannello Notifiche, per questo sta in uno store e non in un composable locale.
//
// Su iOS: solo Safari, da iOS 16.4; fino a iOS 25 (e 17.x fuori UE) PushManager esiste solo nelle PWA installate sulla Home.

type PermessoNotifiche = NotificationPermission;

interface InfoIos {
  èIos: boolean;
  èSafari?: boolean;
  maggiore?: number;
  minore?: number;
}

// Chiave pubblica VAPID (base64 url) nel formato richiesto da pushManager.subscribe
function chiaveVapid(base64: string): Uint8Array<ArrayBuffer> {
  const riempimento = "=".repeat((4 - (base64.length % 4)) % 4);
  const standard = (base64 + riempimento).replace(/-/g, "+").replace(/_/g, "/");
  const grezzo = window.atob(standard);
  const uscita = new Uint8Array(new ArrayBuffer(grezzo.length));
  for (let i = 0; i < grezzo.length; i++) uscita[i] = grezzo.charCodeAt(i);
  return uscita;
}

// iPhone/iPod dallo user agent; iPad con iOS 13+ si presenta come Mac con touch
export function infoIos(userAgent: string, piattaforma: string, puntiTocco: number): InfoIos {
  const èIos = /iPad|iPhone|iPod/.test(userAgent) || (piattaforma === "MacIntel" && puntiTocco > 1);
  if (!èIos) return { èIos: false };
  // Chrome, Firefox ed Edge su iOS usano WebKit ma non hanno accesso alle notifiche push di Apple
  const èSafari = !/CriOS|FxiOS|EdgiOS/.test(userAgent) && /Safari/.test(userAgent);
  const versione = /OS (\d+)[_.](\d+)/.exec(userAgent);
  return { èIos, èSafari, maggiore: versione ? parseInt(versione[1]!, 10) : 0, minore: versione ? parseInt(versione[2]!, 10) : 0 };
}

export type FaseIscrizione = "service_worker" | "iscrizione_browser" | "salvataggio_server";

/** Errore di `iscrivi()` con la fase in cui è fallita; l'errore originale è in `cause`. */
export class ErroreIscrizionePush extends Error {
  constructor(readonly fase: FaseIscrizione, causa: unknown) {
    super(`Attivazione push fallita (${fase}): ${causa instanceof Error ? causa.message : String(causa)}`, { cause: causa });
    this.name = "ErroreIscrizionePush";
  }
}

const SERVICE_WORKER_CANDIDATI = ["/sw-notifications.js"];

export const usePushStore = defineStore("push", () => {
  // Presi nel contesto di setup: dentro le azioni useNuxtApp() non è garantito.
  const { authFetch } = useAuthFetch();
  const chiavePubblica = useRuntimeConfig().public.vapidPublicKey as string | undefined;

  const supportato = ref(false);
  // iOS fuori dalla PWA installata: serve aggiungere l'app alla Home
  const serveInstallazionePwa = ref(false);
  const permesso = ref<PermessoNotifiche>("default");
  const iscrizione = shallowRef<PushSubscription | null>(null);
  const iscritto = ref(false);
  const inCorso = ref(false);

  const puoIscriversi = computed(() => supportato.value && permesso.value === "granted" && !iscritto.value);
  const puoDisiscriversi = computed(() => iscritto.value);
  const servePermesso = computed(() => supportato.value && permesso.value === "default");
  const permessoNegato = computed(() => permesso.value === "denied");
  // Attive = il browser le supporta e questo dispositivo è iscritto
  const attive = computed(() => supportato.value && iscritto.value);

  function controllaSupporto(): boolean {
    if (!import.meta.client) return false;
    serveInstallazionePwa.value = false;
    supportato.value = false;

    const ios = infoIos(navigator.userAgent, navigator.platform, navigator.maxTouchPoints);
    if (ios.èIos) {
      if (!ios.èSafari) return false;
      if ((ios.maggiore ?? 0) < 16 || (ios.maggiore === 16 && (ios.minore ?? 0) < 4)) return false;
      if ("Notification" in window && !("PushManager" in window)) {
        serveInstallazionePwa.value = true;
        return false;
      }
    }

    const ok = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
    supportato.value = ok;
    if (ok) permesso.value = Notification.permission;
    return ok;
  }

  // `navigator.serviceWorker.ready` è l'unico modo corretto di aspettare un service worker attivo (`registration.active` è null
  // durante il passaggio installing → waiting). Timeout di 20 s se il file del service worker è corrotto.
  async function serviceWorkerAttivo(): Promise<ServiceWorkerRegistration> {
    const esistenti = await rimuoviServiceWorkerObsoleti();
    if (esistenti.length === 0) {
      let registrato = false;
      let ultimoErrore: unknown = null;
      for (const url of SERVICE_WORKER_CANDIDATI) {
        try {
          const risposta = await fetch(url, { cache: "no-store" });
          const tipo = (risposta.headers.get("content-type") || "").toLowerCase();
          if (!risposta.ok || tipo.includes("text/html")) continue;
          await navigator.serviceWorker.register(url, { scope: "/" });
          registrato = true;
          break;
        } catch (errore) {
          ultimoErrore = errore;
        }
      }
      if (!registrato) throw ultimoErrore ?? new Error("Nessun Service Worker registrabile trovato");
    }
    return Promise.race([
      navigator.serviceWorker.ready,
      new Promise<never>((_, rifiuta) => setTimeout(() => rifiuta(new Error("Timeout: il Service Worker non si è attivato entro 20s")), 20_000)),
    ]);
  }

  // IMPORTANTE (iOS): va chiamata direttamente da un gesto dell'utente, senza `await` prima
  async function richiediPermesso(): Promise<boolean> {
    if (!supportato.value) throw new Error("Notifiche push non supportate in questo browser");
    let risultato: NotificationPermission;
    try {
      risultato = await Notification.requestPermission();
    } catch {
      // vecchie versioni di Safari: API a callback
      risultato = await new Promise((risolvi) => Notification.requestPermission(risolvi));
    }
    permesso.value = risultato;
    return risultato === "granted";
  }

  async function salvaSuServer(sub: PushSubscription): Promise<void> {
    await authFetch("/api/notifications/subscribe", { body: { subscription: sub, userAgent: navigator.userAgent, timestamp: new Date().toISOString() } });
  }

  async function iscrivi(): Promise<void> {
    if (!supportato.value) throw new Error("Notifiche push non supportate");
    if (permesso.value !== "granted") throw new Error("Permesso notifiche non concesso");
    inCorso.value = true;
    // Fase in corso: finisce nell'errore, per capire in produzione dove si ferma l'attivazione
    let fase: FaseIscrizione = "service_worker";
    try {
      const registrazione = await serviceWorkerAttivo();
      fase = "iscrizione_browser";
      const esistente = await registrazione.pushManager.getSubscription();
      if (esistente) {
        iscrizione.value = esistente;
        iscritto.value = true;
        fase = "salvataggio_server";
        await salvaSuServer(esistente);
        return;
      }
      if (!chiavePubblica) throw new Error("VAPID public key non configurata");
      const nuova = await registrazione.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: chiaveVapid(chiavePubblica) });
      iscrizione.value = nuova;
      iscritto.value = true;
      fase = "salvataggio_server";
      await salvaSuServer(nuova);
    } catch (errore) {
      iscritto.value = false;
      iscrizione.value = null;
      console.error(`[push] attivazione fallita (fase: ${fase}):`, errore);
      throw new ErroreIscrizionePush(fase, errore);
    } finally {
      inCorso.value = false;
    }
  }

  async function disiscrivi(): Promise<void> {
    const corrente = iscrizione.value;
    if (!corrente) return;
    inCorso.value = true;
    try {
      await corrente.unsubscribe();
      await authFetch("/api/notifications/unsubscribe", { body: { subscription: corrente } });
      iscrizione.value = null;
      iscritto.value = false;
    } finally {
      inCorso.value = false;
    }
  }

  async function inviaProva(): Promise<void> {
    if (!iscritto.value) throw new Error("Non iscritto alle notifiche");
    await authFetch("/api/notifications/send-test", { body: { subscription: iscrizione.value } });
  }

  // Al caricamento della pagina: supporto, permesso e iscrizione già esistente (solo su service worker già attivi, senza attendere)
  async function inizializza(): Promise<void> {
    controllaSupporto();
    if (!supportato.value || permesso.value !== "granted") return;
    try {
      const registrazioni = await navigator.serviceWorker.getRegistrations();
      const attiva = registrazioni.find((r) => r.active);
      if (attiva) {
        const sub = await attiva.pushManager.getSubscription();
        iscrizione.value = sub ?? null;
        iscritto.value = !!sub;
      }
    } catch (errore) {
      console.error("[push] errore di inizializzazione:", errore);
      iscritto.value = false;
      iscrizione.value = null;
    }
  }

  return {
    supportato,
    serveInstallazionePwa,
    permesso,
    iscritto,
    inCorso,
    puoIscriversi,
    puoDisiscriversi,
    servePermesso,
    permessoNegato,
    attive,
    inizializza,
    richiediPermesso,
    iscrivi,
    disiscrivi,
    inviaProva,
  };
});
